if (!customElements.get('yg-form-builder')) {
  class YGFormBuilder extends HTMLElement {
    static activeInstance = null;
    static nextId = 0;
    static platforms = [
      ['facebook', 'Facebook'],
      ['tiktok', 'TikTok'],
      ['instagram', 'Instagram'],
      ['youtube', 'YouTube'],
      ['other', 'Other'],
    ];

    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.embed = this.querySelector('.globo-formbuilder');
      this.attempt = null;
      this.candidate = null;
      this.socialFields = new Map();
      this.bioFields = new Map();
      this.preferredRows = new Map();
      this.checkboxLabelState = null;
      this.imagePromises = new Map();
      const successTemplate = this.querySelector('.yg-form-builder__success-template');
      this.successPanel = successTemplate?.content.firstElementChild
        ? document.importNode(successTemplate.content.firstElementChild, true)
        : null;

      this.successPanel?.querySelectorAll('img').forEach((image) => {
        image.loading = 'eager';
        if (image.dataset.src) {
          image.src = image.dataset.src;
          image.removeAttribute('data-src');
        }
        this.imagePromises.set(image, image.decode().then(() => true, () => false));
      });

      this.addEventListener('pointerdown', this.capturePointer, { capture: true, signal });
      this.addEventListener('keydown', this.captureKeyboard, { capture: true, signal });
      this.addEventListener('submit', this.captureSubmit, { capture: true, signal });
      this.addEventListener('input', this.handleFieldInput, { signal });
      this.addEventListener('change', this.handleFieldInput, { signal });
      this.addEventListener('click', this.handleFieldClick, { signal });
      this.addEventListener('keydown', this.handleFieldKeydown, { signal });
      this.addEventListener('reset', this.handleNativeReset, { capture: true, signal });
      this.addEventListener('focusout', this.handleFieldFocusOut, { signal });
      document.addEventListener('click', this.closeOutsideMenu, { signal });
      document.addEventListener('globo.formbuilder.form.loaded', this.handleFormLoaded, { signal });
      document.addEventListener('globo.formbuilder.form.reset', this.handleFormReset, { signal });
      document.addEventListener('globo.formbuilder.form.beforeSubmit', this.handleBeforeSubmit, { signal });
      document.addEventListener('globo.formbuilder.form.submitted', this.handleSubmitted, { signal });
      document.addEventListener('globo.formbuilder.scripts.loaded', this.scheduleInitialization, { signal });
      document.addEventListener('shopify:section:load', this.handleSectionLoad, { signal });
      window.addEventListener('load', this.scheduleInitialization, { signal });
      window.addEventListener('resize', this.updateSuccessSize, { signal });
      if (this.embed) {
        this.fieldObserver = new MutationObserver(this.scheduleEnhancement);
        this.fieldObserver.observe(this.embed, {
          childList: true, subtree: true, attributes: true, attributeFilter: ['aria-invalid', 'maxlength'],
        });
      }
      this.enhanceFields();
      this.scheduleInitialization();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      clearTimeout(this.initTimer);
      clearTimeout(this.resetTimer);
      cancelAnimationFrame(this.layoutFrame);
      this.fieldObserver?.disconnect();
      this.preferredRows.forEach(this.removePreferredRow);
      this.preferredRows.clear();
      this.restoreCheckboxLabel();
      this.closeMenus();
      this.socialFields.forEach(this.removeSocialEnhancement);
      this.socialFields.clear();
      this.bioFields.forEach((counter) => counter.remove());
      this.bioFields.clear();
      this.querySelectorAll('.yg-form-builder__upload-icon, .yg-form-builder__address-icon').forEach((icon) => icon.remove());
      this.querySelectorAll('[data-yg-concerns-hidden]').forEach((field) => field.removeAttribute('data-yg-concerns-hidden'));
      this.restorePage();
      cancelAnimationFrame(this.restoreFrame);
      this.attempt = null;
      this.successPanel = null;
    }

    owns(form) {
      return form instanceof Element && this.embed?.contains(form);
    }

    createIcon(name) {
      const template = this.querySelector(`template[data-yg-form-icon="${name}"]`);
      if (!template?.content.firstElementChild) return null;
      const icon = document.importNode(template.content.firstElementChild, true);
      const ids = new Map();
      icon.querySelectorAll('[id]').forEach((node) => {
        const id = `yg-form-icon-${++YGFormBuilder.nextId}`;
        ids.set(node.id, id);
        node.id = id;
      });
      icon.querySelectorAll('*').forEach((node) => {
        Array.from(node.attributes).forEach((attribute) => {
          let value = attribute.value;
          ids.forEach((id, original) => {
            if (value === `#${original}`) value = `#${id}`;
            value = value.replaceAll(`url(#${original})`, `url(#${id})`);
          });
          if (value !== attribute.value) {
            node.setAttributeNS(attribute.namespaceURI, attribute.name, value);
          }
        });
      });
      return icon;
    }

    scheduleEnhancement = () => {
      if (this.enhanceQueued) return;
      this.enhanceQueued = true;
      queueMicrotask(() => {
        this.enhanceQueued = false;
        if (this.abortController) this.enhanceFields();
      });
    };

    handleFormLoaded = (event) => {
      if (this.owns(event.detail?.form)) this.scheduleEnhancement();
    };

    enhanceFields() {
      if (!this.embed) return;
      this.socialFields.forEach((state, source) => {
        if (!this.embed.contains(source)) {
          this.removeSocialEnhancement(state);
          this.socialFields.delete(source);
        }
      });
      this.bioFields.forEach((counter, textarea) => {
        if (!this.embed.contains(textarea)) {
          counter.remove();
          this.bioFields.delete(textarea);
        }
      });
      this.embed.querySelectorAll('.yg-social-media input[name]').forEach((source) => {
        if (!this.socialFields.has(source)) this.enhanceSocial(source);
      });
      this.embed.querySelectorAll('.yg-professional textarea').forEach((textarea) => {
        if (!this.bioFields.has(textarea)) {
          const counter = document.createElement('span');
          counter.className = 'yg-form-builder__counter';
          textarea.parentElement.append(counter);
          this.bioFields.set(textarea, counter);
        }
        this.updateCounter(textarea);
      });
      this.embed.querySelectorAll('.yg-upload .gfb__dropzone--placeholder').forEach((placeholder) => {
        if (placeholder.querySelector('.yg-form-builder__upload-icon')) return;
        const icon = this.createIcon('upload');
        if (icon) {
          icon.classList.add('yg-form-builder__upload-icon');
          placeholder.prepend(icon);
        }
      });
      this.embed.querySelectorAll('.yg-address .globo-form-input:has(input.classic-input)').forEach((wrapper) => {
        if (wrapper.querySelector('.yg-form-builder__address-icon')) return;
        const icon = this.createIcon('address');
        if (icon) {
          icon.classList.add('yg-form-builder__address-icon');
          wrapper.append(icon);
        }
      });
      this.embed.querySelectorAll('select').forEach(this.updateSelect);
      this.enhancePreferredRows();
      this.enhanceCheckboxLabel();
      this.updateBehavioralConcerns();
      this.embed.querySelectorAll('.action.submit[data-submit-text]:not(.loading)').forEach((button) => {
        const label = new DOMParser().parseFromString(button.dataset.submitText, 'text/html').body.textContent.trim();
        if (label && button.textContent.trim() !== label) {
          const spinner = button.querySelector('.spinner');
          button.replaceChildren(...(spinner ? [spinner] : []), document.createTextNode(label));
        }
      });
      this.socialFields.forEach(this.updateSocialError);
      this.refreshFormLayout();
    }

    updateBehavioralConcerns() {
      this.embed?.querySelectorAll('.yg-describe-concerns').forEach((field) => {
        const group = field.closest('form')?.querySelector('.yg-any-behavioral-concerns');
        if (!group) {
          field.removeAttribute('data-yg-concerns-hidden');
          return;
        }
        const selected = group.querySelector('input[type="radio"]:checked');
        const visible = selected?.value.trim().toUpperCase() === 'YES';
        field.toggleAttribute('data-yg-concerns-hidden', !visible);
        if (visible) return;
        field.querySelectorAll('input:not([type="hidden"]), textarea').forEach((control) => {
          if (!control.value) return;
          control.value = '';
          control.dispatchEvent(new Event('input', { bubbles: true }));
          control.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      this.refreshFormLayout();
    }

    enhancePreferredRows() {
      this.preferredRows.forEach((state, wrapper) => {
        if (!this.embed.contains(wrapper) || state.date.parentElement !== wrapper || state.time.parentElement !== wrapper) {
          this.removePreferredRow(state);
          this.preferredRows.delete(wrapper);
        }
      });
      this.embed.querySelectorAll('.yg-preferred-date').forEach((date) => {
        if (date.parentElement.classList.contains('yg-form-builder__preferred-row')) return;
        const time = date.nextElementSibling;
        if (!time?.matches('.yg-preferred-time')) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'yg-form-builder__preferred-row';
        const control = time.querySelector('select, input');
        const label = time.querySelector('.globo-label');
        const ariaLabel = control?.getAttribute('aria-label');
        if (control && !ariaLabel && !label?.textContent.trim() && label?.dataset.label) {
          control.setAttribute('aria-label', label.dataset.label);
        }
        date.before(wrapper);
        wrapper.append(date, time);
        this.preferredRows.set(wrapper, { wrapper, date, time, control, ariaLabel });
      });
    }

    removePreferredRow = ({ wrapper, control, ariaLabel }) => {
      if (control) {
        if (ariaLabel === null) control.removeAttribute('aria-label');
        else control.setAttribute('aria-label', ariaLabel);
      }
      wrapper.replaceWith(...wrapper.childNodes);
    };

    enhanceCheckboxLabel() {
      const template = this.querySelector('template[data-yg-checkbox-label]');
      const label = template && this.embed.querySelector('.yg-checkbox .checkbox-wrapper .checkbox-label');
      if (this.checkboxLabelState?.label !== label) this.restoreCheckboxLabel();
      if (!label || !template.content.textContent.trim()) return;

      if (!this.checkboxLabelState) {
        const copy = document.createElement('span');
        copy.className = 'yg-form-builder__checkbox-copy';
        copy.append(template.content.cloneNode(true));
        this.checkboxLabelState = {
          label, copy,
          originalNodes: Array.from(label.childNodes),
          ariaLabel: label.getAttribute('aria-label'),
        };
      }
      const { copy } = this.checkboxLabelState;
      if (label.childNodes.length !== 1 || label.firstChild !== copy) label.replaceChildren(copy);
      label.removeAttribute('aria-label');
    }

    restoreCheckboxLabel() {
      const state = this.checkboxLabelState;
      if (!state) return;
      state.label.replaceChildren(...state.originalNodes);
      if (state.ariaLabel === null) state.label.removeAttribute('aria-label');
      else state.label.setAttribute('aria-label', state.ariaLabel);
      this.checkboxLabelState = null;
    }

    refreshFormLayout() {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => {
        const form = this.embed?.querySelector('form');
        const wizard = window.Globo?.FormBuilder?.forms?.[form?.dataset.id]?.wizardInstance;
        wizard?.panels?.updatePanelsContainerHeight?.();
      });
    }

    updateCounter(textarea) {
      const counter = this.bioFields.get(textarea);
      if (!counter) return;
      const count = textarea.value.length;
      const text = textarea.maxLength >= 0 ? `${count} / ${textarea.maxLength}` : String(count);
      if (counter.textContent !== text) counter.textContent = text;
    }

    updateSelect = (select) => {
      const empty = !select.value;
      select.classList.toggle('yg-form-builder__select-empty', empty);
      select.parentElement.querySelector('.nice-select')?.classList.toggle('yg-form-builder__select-empty', empty);
    };

    enhanceSocial(source) {
      const field = source.closest('.yg-social-media');
      const wrapper = document.createElement('div');
      wrapper.className = 'yg-form-builder__social';
      const list = document.createElement('div');
      list.className = 'yg-form-builder__social-list';
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'yg-form-builder__social-add';
      add.setAttribute('aria-label', 'Add social media profile');
      const icon = this.createIcon('plus');
      if (icon) add.append(icon);
      wrapper.append(list, add);
      const state = {
        source, field, wrapper, list, add,
        tabindex: source.getAttribute('tabindex'),
        ariaHidden: source.getAttribute('aria-hidden'),
      };
      this.socialFields.set(source, state);
      source.classList.add('yg-form-builder__social-source');
      source.tabIndex = -1;
      source.setAttribute('aria-hidden', 'true');
      source.after(wrapper);
      // Preserve draft values when an editor section reconnects.
      const entries = source.value.split('；').filter((entry) => entry.trim());
      if (entries.length) {
        entries.forEach((entry) => {
          const match = entry.match(/^\d+\.\s*(Facebook|TikTok|Instagram|YouTube|Other)：([\s\S]*)$/);
          this.addSocialRow(state, match ? match[1].toLowerCase() : 'other', match ? match[2] : entry);
        });
      } else {
        this.addSocialRow(state);
      }
      this.syncSocial(state, false);
    }

    removeSocialEnhancement = (state) => {
      state.wrapper.remove();
      state.source.classList.remove('yg-form-builder__social-source');
      for (const [attribute, value] of [['tabindex', state.tabindex], ['aria-hidden', state.ariaHidden]]) {
        if (value === null) state.source.removeAttribute(attribute);
        else state.source.setAttribute(attribute, value);
      }
    };

    addSocialRow(state, platform = 'facebook', value = '') {
      const row = document.createElement('div');
      row.className = 'yg-form-builder__social-row';
      row.dataset.platform = platform;
      const selector = document.createElement('div');
      selector.className = 'yg-form-builder__platform';
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'yg-form-builder__platform-trigger';
      trigger.setAttribute('aria-haspopup', 'menu');
      trigger.setAttribute('aria-expanded', 'false');
      const menu = document.createElement('div');
      menu.className = 'yg-form-builder__platform-menu';
      menu.id = `yg-form-platform-${++YGFormBuilder.nextId}`;
      menu.role = 'menu';
      menu.setAttribute('aria-label', 'Social media platform');
      menu.hidden = true;
      trigger.setAttribute('aria-controls', menu.id);
      YGFormBuilder.platforms.forEach(([key, label]) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'yg-form-builder__platform-option';
        option.dataset.platform = key;
        option.role = 'menuitemradio';
        option.tabIndex = -1;
        option.setAttribute('aria-label', label);
        option.title = label;
        const icon = this.createIcon(key);
        if (icon) option.append(icon);
        menu.append(option);
      });
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'yg-form-builder__social-url';
      input.placeholder = 'Enter profile URL';
      input.setAttribute('aria-label', 'Social media profile URL');
      input.inputMode = 'url';
      input.autocomplete = 'url';
      input.value = value;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'yg-form-builder__social-remove';
      remove.setAttribute('aria-label', 'Remove social media profile');
      const icon = this.createIcon('minus');
      if (icon) remove.append(icon);
      selector.append(trigger, menu);
      row.append(selector, input, remove);
      state.list.append(row);
      this.setPlatform(row, platform);
      return row;
    }

    setPlatform(row, key) {
      const label = YGFormBuilder.platforms.find(([platform]) => platform === key)[1];
      row.dataset.platform = key;
      const trigger = row.querySelector('.yg-form-builder__platform-trigger');
      trigger.setAttribute('aria-label', `Platform: ${label}`);
      trigger.title = label;
      const icon = this.createIcon(key);
      trigger.replaceChildren(...(icon ? [icon] : [label]));
      row.querySelectorAll('.yg-form-builder__platform-option').forEach((option) => {
        option.setAttribute('aria-checked', String(option.dataset.platform === key));
      });
    }

    syncSocial(state, notify = true) {
      const entries = Array.from(state.list.children).flatMap((row) => {
        const url = row.querySelector('input').value.trim();
        const label = YGFormBuilder.platforms.find(([key]) => key === row.dataset.platform)[1];
        return url ? [`${label}：${url}`] : [];
      });
      const value = entries.map((entry, index) => `${index + 1}. ${entry}`).join('；');
      if (state.source.value !== value) {
        state.source.value = value;
        if (notify) state.source.dispatchEvent(new Event('input', { bubbles: true }));
      }
      // The app also binds change listeners to unnamed inputs. Reconcile its
      // field error against the named source even when a URL only contains spaces.
      if (notify) state.source.dispatchEvent(new Event('change', { bubbles: true }));
      this.updateSocialError(state);
      this.refreshFormLayout();
    }

    updateSocialError = (state) => {
      const invalid = state.source.getAttribute('aria-invalid') === 'true';
      const description = state.source.getAttribute('aria-describedby');
      state.wrapper.querySelectorAll('input, button').forEach((control) => {
        if (description) control.setAttribute('aria-describedby', description);
        if (control.matches('input, .yg-form-builder__social-add')) {
          if (invalid && control.getAttribute('aria-invalid') !== 'true') control.setAttribute('aria-invalid', 'true');
          else if (!invalid && control.hasAttribute('aria-invalid')) control.removeAttribute('aria-invalid');
        }
      });
    };

    handleFieldInput = (event) => {
      if (this.bioFields.has(event.target)) this.updateCounter(event.target);
      if (event.target.matches('select')) this.updateSelect(event.target);
      if (event.target.matches('.yg-any-behavioral-concerns input[type="radio"]')) this.updateBehavioralConcerns();
      if (!event.target.matches('.yg-form-builder__social-url')) return;
      const state = this.socialFields.get(event.target.closest('.yg-social-media').querySelector('input[name]'));
      if (state) this.syncSocial(state);
    };

    handleFieldClick = (event) => {
      const field = event.target.closest('.yg-social-media');
      const state = field && this.socialFields.get(field.querySelector('input[name]'));
      if (!state) return;
      const button = event.target.closest('button');
      const row = button?.closest('.yg-form-builder__social-row');
      if (button === state.add) {
        this.closeMenus();
        this.addSocialRow(state).querySelector('input').focus();
        this.syncSocial(state);
      } else if (button?.matches('.yg-form-builder__social-remove')) {
        const next = row.nextElementSibling || row.previousElementSibling;
        row.remove();
        (next?.querySelector('input') || state.add).focus();
        this.syncSocial(state);
      } else if (button?.matches('.yg-form-builder__platform-trigger')) {
        const open = button.getAttribute('aria-expanded') !== 'true';
        this.closeMenus();
        if (open) this.openMenu(row);
      } else if (button?.matches('.yg-form-builder__platform-option')) {
        this.setPlatform(row, button.dataset.platform);
        this.closeMenus();
        row.querySelector('.yg-form-builder__platform-trigger').focus();
        this.syncSocial(state);
      } else if (event.target.closest('label')?.htmlFor === state.source.id) {
        event.preventDefault();
        (state.list.querySelector('input') || state.add).focus();
      }
    };

    openMenu(row, last = false) {
      row.querySelector('.yg-form-builder__platform-trigger').setAttribute('aria-expanded', 'true');
      const menu = row.querySelector('.yg-form-builder__platform-menu');
      menu.hidden = false;
      const options = Array.from(menu.children);
      (last ? options.at(-1) : menu.querySelector('[aria-checked="true"]') || options[0]).focus();
    }

    closeMenus() {
      this.querySelectorAll('.yg-form-builder__platform-trigger[aria-expanded="true"]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.nextElementSibling.hidden = true;
      });
    }

    closeOutsideMenu = (event) => {
      if (!this.contains(event.target)) this.closeMenus();
      else if (!event.target.closest('.yg-form-builder__platform')) this.closeMenus();
    };

    handleFieldFocusOut = (event) => {
      const selector = event.target.closest('.yg-form-builder__platform');
      if (selector && !selector.contains(event.relatedTarget)) this.closeMenus();
    };

    handleFieldKeydown = (event) => {
      const selector = event.target.closest('.yg-form-builder__platform');
      if (!selector) return;
      const row = selector.parentElement;
      const trigger = selector.querySelector('.yg-form-builder__platform-trigger');
      const menu = selector.querySelector('.yg-form-builder__platform-menu');
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeMenus();
        trigger.focus();
      } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        if (menu.hidden) {
          this.closeMenus();
          this.openMenu(row, event.key === 'ArrowUp' || event.key === 'End');
        } else {
          const options = Array.from(menu.children);
          const index = options.indexOf(document.activeElement);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1
            : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
          options[next].focus();
        }
      }
    };

    handleNativeReset = (event) => {
      if (!this.owns(event.target)) return;
      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => {
        if (!event.defaultPrevented && this.abortController) this.resetFields();
      }, 0);
    };

    handleFormReset = (event) => {
      if (this.owns(event.detail?.form)) {
        clearTimeout(this.resetTimer);
        this.resetFields();
      }
    };

    resetFields() {
      this.closeMenus();
      this.socialFields.forEach((state) => {
        state.list.replaceChildren();
        this.addSocialRow(state);
        this.syncSocial(state, false);
      });
      this.bioFields.forEach((counter, textarea) => this.updateCounter(textarea));
      this.embed?.querySelectorAll('select').forEach(this.updateSelect);
      this.updateBehavioralConcerns();
    }

    snapshot(form, submitter) {
      return {
        form,
        left: window.scrollX,
        top: window.scrollY,
        focus: submitter || form.querySelector('button[type="submit"], input[type="submit"], .action.submit'),
        notices: Array.from(this.embed.querySelectorAll('[data-message-success], .message.success'), (node) => ({
          node,
          display: node.style.getPropertyValue('display'),
          priority: node.style.getPropertyPriority('display'),
        })),
      };
    }

    capturePointer = (event) => {
      const button = event.target.closest('button[type="submit"], input[type="submit"], .action.submit');
      const form = button?.closest('form');
      if (event.button !== 0 || !this.owns(form) || button.disabled) return;
      this.candidate = { ...this.snapshot(form, button), time: performance.now() };
    };

    captureKeyboard = (event) => {
      if (event.key !== 'Enter' || event.isComposing || event.target.closest('textarea')) return;
      const form = event.target.closest('form');
      if (this.owns(form)) {
        this.candidate = { ...this.snapshot(form), time: performance.now() };
      }
    };

    captureSubmit = (event) => {
      if (!this.owns(event.target)) return;
      this.socialFields.forEach((state) => this.syncSocial(state));
      const candidate = this.candidate;
      this.attempt = candidate?.form === event.target && performance.now() - candidate.time < 1000
        ? candidate
        : this.snapshot(event.target, event.submitter);
      this.candidate = null;
    };

    handleBeforeSubmit = (event) => {
      const form = event.detail?.form;
      if (this.owns(form) && (!this.attempt || this.attempt.completed || this.attempt.form !== form)) {
        this.attempt = this.snapshot(form);
      }
    };

    handleSubmitted = async (event) => {
      const form = event.detail?.form;
      if (!this.owns(form) || !this.successPanel || YGFormBuilder.activeInstance) return;
      const attempt = this.attempt?.form === form ? this.attempt : this.snapshot(form);
      if (attempt.completed) return;
      this.attempt = attempt;
      attempt.completed = true;

      const signal = this.abortController.signal;
      const mobile = window.matchMedia('(max-width: 767.98px)').matches;
      const images = this.successPanel.querySelectorAll('img');
      const image = images.length === 1
        ? images[0]
        : this.successPanel.querySelector(mobile ? 'img.hide-pc' : 'img.hide-mobile');
      if (!image) return;

      let imageTimer;
      let abort;
      const ready = await Promise.race([
        this.imagePromises.get(image),
        new Promise((resolve) => {
          imageTimer = setTimeout(() => resolve(false), 5000);
          abort = () => resolve(false);
          signal.addEventListener('abort', abort, { once: true });
        }),
      ]);
      clearTimeout(imageTimer);
      signal.removeEventListener('abort', abort);
      if (!ready || signal.aborted || this.attempt !== attempt || YGFormBuilder.activeInstance) return;

      this.showSuccess(attempt);
    };

    showSuccess(attempt) {
      YGFormBuilder.activeInstance = this;
      this.returnState = attempt;
      this.hiddenNodes = new Map();
      this.successPanel.style.setProperty(
        '--yg-form-builder-background',
        getComputedStyle(this).getPropertyValue('--yg-form-builder-background') || '#000000',
      );
      document.body.append(this.successPanel);
      document.documentElement.classList.add('yg-form-builder-success-active');
      Array.from(document.body.children).forEach(this.hidePageNode);
      this.pageObserver = new MutationObserver((records) => {
        records.forEach((record) => record.addedNodes.forEach(this.hidePageNode));
      });
      this.pageObserver.observe(document.body, { childList: true });
      this.updateSuccessSize();
      this.successPanel.focus({ preventScroll: true });
      window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
      const seconds = Number(this.dataset.successDuration);
      const duration = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 5000;
      this.successFrame = requestAnimationFrame(() => {
        this.successTimer = setTimeout(() => this.restorePage(), duration);
      });
    }

    hidePageNode = (node) => {
      if (!(node instanceof HTMLElement) || node === this.successPanel || this.hiddenNodes.has(node)) return;
      if (node.matches('.shopify-section-group-header-group, script, style, link, template, #PBarNextFrameWrapper')) return;
      this.hiddenNodes.set(node, {
        inert: node.getAttribute('inert'),
        marker: node.getAttribute('data-yg-form-builder-hidden'),
      });
      node.setAttribute('data-yg-form-builder-hidden', '');
      node.inert = true;
    };

    updateSuccessSize = () => {
      if (YGFormBuilder.activeInstance !== this) return;
      const height = Array.from(document.body.children)
        .filter((node) => node.matches('.shopify-section-group-header-group'))
        .reduce((total, node) => total + node.getBoundingClientRect().height, 0);
      this.successPanel.style.setProperty('--yg-form-builder-header-height', `${height}px`);
    };

    restorePage() {
      clearTimeout(this.successTimer);
      cancelAnimationFrame(this.successFrame);
      if (YGFormBuilder.activeInstance !== this) return;
      this.pageObserver?.disconnect();
      this.hiddenNodes.forEach((state, node) => {
        for (const [name, value] of [['inert', state.inert], ['data-yg-form-builder-hidden', state.marker]]) {
          if (value === null) node.removeAttribute(name);
          else node.setAttribute(name, value);
        }
      });
      this.hiddenNodes.clear();
      this.successPanel?.remove();
      document.documentElement.classList.remove('yg-form-builder-success-active');
      YGFormBuilder.activeInstance = null;

      const state = this.returnState;
      this.returnState = null;
      state.notices.forEach(({ node, display, priority }) => {
        if (display) node.style.setProperty('display', display, priority);
        else node.style.removeProperty('display');
      });
      if (state.focus?.isConnected) state.focus.focus({ preventScroll: true });
      const restoreScroll = () => window.scrollTo({ left: state.left, top: state.top, behavior: 'instant' });
      restoreScroll();
      this.restoreFrame = requestAnimationFrame(() => {
        if (!YGFormBuilder.activeInstance) restoreScroll();
      });
    }

    scheduleInitialization = () => {
      clearTimeout(this.initTimer);
      this.initTimer = setTimeout(() => this.initializeForm(), 0);
    };

    handleSectionLoad = (event) => {
      if (event.target.contains(this)) this.scheduleInitialization();
    };

    async initializeForm() {
      const api = window.Globo?.FormBuilder;
      if (!this.embed || this.embed.querySelector('form') || this.initializing || typeof api?.init !== 'function') return;
      let formId = this.dataset.formId;
      if (!/^\d+$/.test(formId)) {
        try {
          formId = atob(formId);
        } catch {
          return;
        }
      }
      if (!/^\d+$/.test(formId)) return;
      this.initializing = true;
      const signal = this.abortController.signal;
      try {
        if (!api.forms?.[formId] && api.assetFormUrls?.[formId]) {
          const url = new URL(api.assetFormUrls[formId], location.href);
          if (url.protocol !== 'https:') return;
          await import(url.href);
        }
        if (signal.aborted || !api.forms?.[formId] || this.embed.querySelector('form')) return;
        this.embed.dataset.id = formId;
        this.embed.id ||= `globo-formbuilder-${formId}`;
        await api.init(this.embed, formId);
      } catch (error) {
        console.warn('YG Form builder could not initialize this form.', error);
      } finally {
        this.initializing = false;
      }
    }
  }

  customElements.define('yg-form-builder', YGFormBuilder);
}
